<template>
  <div
    class="login"
  >
    <h1>
      This is a login page
    </h1>

    <div
      class="section host"
    >
      <h3>
        Host
      </h3>
      <div
        class="description"
      >
        <p>
          The host of the Hydrus API, including <code>http://</code> or
          <code>https://</code>. It should not end with a <code>/</code>.
        </p>
        <p>
          See
          <a
            href="https://hydrusnetwork.github.io/hydrus/client_api.html#enabling_the_api"
          >Hydrus Documentation: Enabling the Client API</a>.
        </p>
      </div>
      <div
        class="host-input-wrapper"
      >
        <input
          :name="`host[${hostShortname}]`"
          v-model="host"
        >
        <RecentHostsDropdown
          @select="host = $event"
        />
      </div>
    </div>

    <div
      class="section apiKey"
    >
      <h3>
        Api Key
      </h3>
      <div
        class="description"
      >
        <p>
          The Api Key of the Hydrus API.
        </p>
        <p>
          To quote
          <a
            href="https://hydrusnetwork.github.io/hydrus/client_api.html#enabling_the_api"
          >Hydrus Documentation: Enabling the Client API</a>:
        </p>
        <blockquote>
          Once the API is running, go to its entry in
          <code>services</code>-><code>review services</code>. Each external
          program trying to access the API will need its own access key, which
          is the familiar 64-character hexadecimal used in many places in
          hydrus.
        </blockquote>
        <p>
          Go there, and create a new api client.<br>
          The name you can set however you want. What about <code>Hydrus Client ({{ hostShortname }})</code>?<br>
          For the permissions you need the following ones:
          <ul>
            <li>
              <code>
                edit file ratings
              </code><br>
              This is needed if you use the upvote/downvote buttons.
            </li>
            <li>
              <code>
                manage pages
              </code><br>
              With this it can access the current open page, to know which images to display. It will also allow you to hit the reload button, rerunning a file search or similar.
            </li>
            <li>
              <code>
                search and fetch files
              </code><br>
              This is needed to allow to download and display the images.
            </li>
            <li>
              <code>
                import and delete files
              </code>
              <br>
              Allows to set the archival state.
            </li>
            <li>
              <code>
                manage popups
              </code><br>
              Allows to open the current image in the Hydrus Client. It will show you a notification popup similar to a finished subscription, clicking it will show the file(s).
            </li>
          </ul>
        </p>
      </div>
      <input
        :name="`api-key[${hostShortname}]`"
        type="password"
        v-model="apiKey"
        placeholder="-enter your api key here-"
      >
      <br>
      <p>
        Your current SessionKey is: <code>{{ apiConfig.sessionKey }}</code>
      </p>
    </div>
    <div
      class="section submit"
    >
      <button
        type="button"
        @click="checkApi()"
      >
        Store
      </button>
    </div>
    <br>
    <br>
    <hr>
    <br> <!-- todo: solve with css instead of <br> -->
    <br>
    <h2>
      This are initial settings
    </h2>
    <div
      class="section tagServiceId"
    >
      <h3>
        Tag Service Ids
      </h3>
      <h4>
        Tag Display Service
      </h4>
      <div
        class="description"
      >
        <p>
          Allows you to configure you the tag service used for
          <b>displaying</b> the tags of an image.
          Defaults to the <i>virtual combined tag service</i>, which merges all existing tag services.
        </p>
        <p>
          See
          <a
            href="https://hydrusnetwork.github.io/hydrus/getting_started_more_tags.html#tag_services"
          >Hydrus Documentation: Tag services</a>.
        </p>
      </div>
      <div>
        <input
          v-if="!tagReadServices"
          :name="`tagReadServiceId[${hostShortname}]`"
          v-model="tagReadServiceId"
          placeholder="Load services to use dropdown"
        >
        <select
          v-else
          :name="`tagReadServiceId[${hostShortname}]`"
          v-model="tagReadServiceId"
        >
          <option
            value=""
          >
            — Select a service or enter ID manually —
          </option>
          <option
            v-for="service in tagReadServices"
            :key="service.service_key"
            :value="service.service_key"
          >
            {{ service.name }}
          </option>
        </select>
      </div>
      <h4>
        Tag Storage Service
      </h4>
      <div
        class="description"
      >
        <p>
          Allows you to configure you the tag service used for
          <b>changing</b> the tags of an image.
          Defaults to the <code>my tags</code> <i>local tag service</i>.
        </p>
        <p>
          Add Tags permission needed.
        </p>
        <p>
          See
          <a
            href="https://hydrusnetwork.github.io/hydrus/getting_started_more_tags.html#my_tags"
          >Hydrus Documentation: My Tags</a>.
        </p>
      </div>
      <div>
        <input
          v-if="!tagWriteServices"
          :name="`tagWriteServiceId[${hostShortname}]`"
          v-model="tagWriteServiceId"
          placeholder="Load services to use dropdown"
        >
        <select
          v-else
          :name="`tagWriteServiceId[${hostShortname}]`"
          v-model="tagWriteServiceId"
        >
          <option
            value=""
          >
            — Select a service or enter ID manually —
          </option>
          <option
            v-for="service in tagWriteServices"
            :key="service.service_key"
            :value="service.service_key"
          >
            {{ service.name }}
          </option>
        </select>
      </div>
    </div>

    <div
      class="section voteServiceIds"
    >
      <h3>
        Rating Service Ids
      </h3>
      <div
        class="description"
      >
        <p>
          Allows you to configure you the rating services used for
          upvote/downvotes, faves, star ratings, etc.
        </p>
        <p>
          See
          <a
            href="https://hydrusnetwork.github.io/hydrus/getting_started_ratings.html"
          >Hydrus Documentation: Ratings</a>.
        </p>
        <p>
          They can be created in <code>services</code>-><code>magage services</code>, one of those <code>… rating service</code> options.
        </p>
        <p>
          After you created them, head over to <code>services</code>-><code>review services</code>, click on the tab with your service, and hit
          <kbd>
            copy service key
          </kbd> to kopy copy the id of that to the
          clipboard and paste it here to the list.
        </p>
        <p>
          Specify one per line. They will all be used, spilling to the next one if you keep voting up.<br>
        </p>
        <p>
          This will be used in the <b>easy upvote system</b>:<br>
        </p>
      </div>
      <button
        type="button"
        class="configure-btn"
        @click="showVoteServicePanel = true"
      >
        Configure vote services…
      </button>
      <VoteServiceConfigPanel
        v-if="showVoteServicePanel"
        @close="showVoteServicePanel = false"
      />
      <div>
        <label>
          Auto-archive:
          <select
            :name="`archivalMode[${hostShortname}]`"
            v-model="apiConfig.archivalMode"
            class="archival-mode-select"
          >
            <option
              value="never"
            >Never</option>
            <option
              value="always-archive"
            >Always archive</option>
            <option
              value="always-unarchive"
            >Always unarchive</option>
            <option
              value="vote-exists"
            >Archive if voted</option>
            <option
              value="vote-exists-both"
            >Archive if voted, else unarchive</option>
            <option
              value="positive-vote"
            >Archive if any positive vote</option>
            <option
              value="positive-vote-or-unarchive"
            >Archive if any positive vote, else unarchive</option>
            <option
              value="custom"
            >Custom</option>
          </select>
        </label>
      </div>
      <div
        v-if="apiConfig.archivalMode === 'custom'"
        class="custom-rules-editor"
      >
        <VoteRuleEditor />
      </div>
    </div>

    <div
      class="section check"
    >
      <h3>
        Connection Check
      </h3>
      <span
        v-if="apiConfig.sessionKey"
      >✅ looks good!</span>
      <span
        v-else
      >❌ not connected.</span>
    </div>
    <div
      class="section goto"
    >
      <button
        type="button"
        :disabled="!apiConfig.sessionKey"
        @click="goto()"
      >
        go to image viewer
      </button>
    </div>
    <div
      class="section keybindings"
    >
      <h3>
        Keybindings
      </h3>
      <p>
        Here's a list of keybindings:
        <ul>
          <li>
            <kbd>
              ←
            </kbd>/<kbd>
              →
            </kbd>
            —
            Left/Right arrow keys for navigating through the images.
          </li>
          <li>
            <kbd>
              ↑
            </kbd>/<kbd>
              ↓
            </kbd>
            —
            Up/Down arrow keys for voting up or down.<br>
            This also opens the easy-vote-system, if not yet open.
          </li>
          <li>
            <kbd>
              ↵
            </kbd>/<kbd>
              ⌤
            </kbd>
            —
            The Enter key will submit the vote and close the easy-vote-system.<br>
            If it isn't open yet, it will be opened.
          </li>
          <li>
            <kbd>
              ⇧
            </kbd>/<kbd>
              esc
            </kbd>
            —
            Shift or Escape keys will abort and close the easy-vote-system.
          </li>
        </ul>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useApiStore } from "@/stores/api";
import { useRecentHostsStore } from "@/stores/recentHosts";
import VoteRuleEditor from "@/components/rules/VoteRuleEditor.vue";
import VoteServiceConfigPanel from "@/components/rules/VoteServiceConfigPanel.vue";
import RecentHostsDropdown from "@/components/RecentHostsDropdown.vue";
import {
  API_HOST,
  API_KEY,
  setLocalStorageItem,
} from "@/stores/localStorage";
import { useRoute, useRouter } from "vue-router";

const apiConfig = useApiStore();
const recentHostsStore = useRecentHostsStore();
const router = useRouter();
const route = useRoute();

const host = ref<string>(apiConfig.host || "localhost");
const apiKey = ref<string>(apiConfig.apiKey || "");
const tagReadServiceId = ref<string>(apiConfig.tagReadServiceId || "");
const tagWriteServiceId = ref<string>(apiConfig.tagWriteServiceId || "");
const hostShortname = ref(`${location.protocol}//${location.host}`);
const showVoteServicePanel = ref(false);
const isAutoLoggingIn = ref(false);

// Computed properties for tag service dropdowns (filter by type)
const tagReadServices = computed(() =>
  apiConfig.availableServices
    ? Object.values(apiConfig.availableServices).filter((s) => s.type === 5 || s.type === 10)
    : null,
);

const tagWriteServices = computed(() =>
  apiConfig.availableServices
    ? Object.values(apiConfig.availableServices).filter((s) => s.type === 5)
    : null,
);

// Auto-login on mount if credentials are set, then load available services
onMounted(async () => {
  if (apiConfig.host && apiConfig.apiKey && !apiConfig.sessionKey) {
    isAutoLoggingIn.value = true;
    try {
      await apiConfig.ensureSession();
      await apiConfig.refreshAvailableServices();
    } finally {
      isAutoLoggingIn.value = false;
    }
  } else if (!apiConfig.availableServices && apiConfig.sessionKey) {
    // If session is already loaded but services haven't been fetched yet, fetch them
    await apiConfig.refreshAvailableServices();
  }
});

async function checkApi() {
  apiConfig.host = host.value;
  apiConfig.apiKey = apiKey.value;
  apiConfig.tagReadServiceId = tagReadServiceId.value;
  apiConfig.tagWriteServiceId = tagWriteServiceId.value;
  setLocalStorageItem(API_HOST, host.value);
  setLocalStorageItem(API_KEY, apiKey.value);
  await apiConfig.ensureSession();
  // Add host to recent hosts on successful login
  recentHostsStore.addHost(host.value);
  // do not redirect (e.g. `goto()`) as we need to be able to change values which depend on the connection here.
}

function goto() {
  router.push({
    name: "home",
    query: route.query,
  });
}
</script>

<style scoped lang="scss">
:root {
  /* Start with axis defaults. Exclude weight, which is better controlled via the usual font-weight CSS property */
  --f-wght: 0; /* Boldness */
  --f-mono: 0; /* Monospace, 0/1 */
  --f-casu: 0.5; /* Casual: 0/1      */
  --f-slnt: 0; /* Upright      */
  --f-crsv: 0.5; /* Auto Cursive */
  --f-size: 12px;
}

html,
body {
  width: 100%;
}
* {
  font-family: "RecVar", "Monofonto", sans-serif;
}
*,
*:before,
*:after {
  font-weight: var(--f-wght);
  font-size: var(--f-size);
  font-variation-settings: "MONO" var(--f-mono), "CASL" var(--f-casu),
    "slnt" var(--f-slnt), "CRSV" var(--f-crsv);
}
h1, h2, h3, h4 {
  transition: 0.75s;
  --f-wght: 1000;
  --f-casu: 0;
}
h1 {
  --f-size: 40px;
}
h2 {
  --f-size: 30px;
}
h3 {
  --f-size: 18px;
}
h4 {
  --f-size: 14px;
}
h3:hover, h4:hover {
  --f-casu: 1;
}
blockquote {
  --f-casu: 1;
}
code {
  --f-mono: 1;
  --f-casu: 0;
  --f-wght: 200;
}

.login {
  left: 0;
  right: 0;
  margin-left: 1em;
  margin-right: 1em;
}
.login * {
  width: auto;
}

.login input[type="text"],
.login input[type="password"],
.login input:not([type="checkbox"]):not([type="radio"]) {
  background: var(--bg-elevated);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  width: 100%;
  display: inline-block;
  --f-size: 11px;
  padding: 6px 8px;
}
.section {
  margin-left: 1em;
  margin-right: 1em;
  margin-bottom: 1em;
}
.section h3,
.section.submit {
  margin-left: -0.5em;
}

.section.voteServiceIds input {
  width: calc(
    64 * var(--f-size) * 0.58 + 5px
  ); // chars * font-size * font-size-to-char-width-ratio + space after char
}

.section.submit button,
.section.goto button,
.configure-btn {
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  padding: 8px 16px;
  cursor: pointer;
  font-size: 1rem;
  transition: background var(--transition-fast);

  &:hover { background: var(--accent-hover); }
  &:disabled { opacity: 0.45; cursor: default; }
}

.login input[type="checkbox"],
.section.voteServiceIds input[type="checkbox"]{
  width: initial;
  display: inline;
}

.host-input-wrapper {
  display: flex;
  gap: 6px;
  align-items: center;

  input {
    flex: 1;
  }
}
</style>
